import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import Spinner from './Spinner';
import TrialExpiredMessage from './TrialExpiredMessage';
import { FiClock, FiStar, FiCheck } from 'react-icons/fi';

const AccountModal = ({ isOpen, onClose, onUpgradeClick }) => {
  const { userInfo, userInfoLoading, firebaseUser, fetchUserInfo } = useAuth();
  const trialStatus = useTrialStatus(userInfo);

  // Debug logging
  // console.log('AccountModal: isOpen:', isOpen);
  // console.log('AccountModal: userInfoLoading:', userInfoLoading);
  // console.log('AccountModal: userInfo:', userInfo);
  // console.log('AccountModal: firebaseUser:', firebaseUser ? firebaseUser.uid : 'no user');
  // console.log('AccountModal: trialStatus:', trialStatus);

  if (!isOpen) return null;

  const handleRetryFetch = () => {
    // console.log('AccountModal: Retry button clicked');
    if (firebaseUser) {
      fetchUserInfo(firebaseUser);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Account Information</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Loading State */}
          {userInfoLoading && (
            <div className="flex items-center justify-center py-8">
              <Spinner />
              <span className="ml-2 text-gray-600">Loading account information...</span>
            </div>
          )}

          {/* Loading taking too long - show retry option */}
          {userInfoLoading && (
            <div className="text-center mt-4">
              <button
                onClick={handleRetryFetch}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Taking too long? Click to retry
              </button>
            </div>
          )}

          {/* User Information */}
          {!userInfoLoading && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium">{userInfo?.displayName || firebaseUser?.displayName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium">{userInfo?.email || firebaseUser?.email || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Member Since:</span>
                    <span className="font-medium">{formatDate(userInfo?.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auth Method:</span>
                    <span className="font-medium capitalize">{userInfo?.method || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Age Confirmed:</span>
                    <span className="font-medium">{userInfo?.ageConfirmed ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Trial Status */}
              {userInfo && (
                <div className="pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Status</h3>
                  <div className="space-y-3">
                    {trialStatus.isPaidUser && (
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-700 mb-2">
                          <FiStar className="text-amber-500" size={18} />
                          <span className="font-semibold">Premium Member</span>
                        </div>
                        <p className="text-sm text-amber-600">You have unlimited access to all features</p>
                        {userInfo.subscriptionPeriodEnd && (
                          <p className="text-xs text-amber-600 mt-1">
                            Next billing: {formatDate(userInfo.subscriptionPeriodEnd)}
                          </p>
                        )}
                      </div>
                    )}

                    {trialStatus.isTrialUser && !trialStatus.isTrialExpired && (
                      <div className={`rounded-lg p-3 ${
                        trialStatus.daysRemaining <= 1 
                          ? 'bg-gradient-to-r from-red-50 to-rose-50 border border-red-200'
                          : trialStatus.daysRemaining <= 3
                          ? 'bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200'
                          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
                      }`}>
                        <div className={`flex items-center gap-2 mb-2 ${
                          trialStatus.daysRemaining <= 1 ? 'text-red-700' :
                          trialStatus.daysRemaining <= 3 ? 'text-orange-700' :
                          'text-blue-700'
                        }`}>
                          <FiClock className={
                            trialStatus.daysRemaining <= 1 ? 'text-red-500' :
                            trialStatus.daysRemaining <= 3 ? 'text-orange-500' :
                            'text-blue-500'
                          } size={18} />
                          <span className="font-semibold">Free Trial Active</span>
                        </div>
                        <div className="space-y-1">
                          <p className={`text-sm ${
                            trialStatus.daysRemaining <= 1 ? 'text-red-600' :
                            trialStatus.daysRemaining <= 3 ? 'text-orange-600' :
                            'text-blue-600'
                          }`}>
                            {trialStatus.daysRemaining === 1 
                              ? 'Last day of your free trial!' 
                              : `${trialStatus.daysRemaining} days remaining`}
                          </p>
                          {trialStatus.trialEndDate && (
                            <p className="text-xs text-gray-500">
                              Trial ends: {formatDate(trialStatus.trialEndDate)}
                            </p>
                          )}
                        </div>
                        {onUpgradeClick && (
                          <button
                            onClick={onUpgradeClick}
                            className={`mt-3 w-full text-sm font-semibold py-2 px-3 rounded transition-all duration-200 ${
                              trialStatus.daysRemaining <= 1 
                                ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white'
                                : trialStatus.daysRemaining <= 3
                                ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
                                : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white'
                            }`}
                          >
                            Upgrade to Premium
                          </button>
                        )}
                      </div>
                    )}

                    {trialStatus.isTrialUser && trialStatus.isTrialExpired && (
                      <TrialExpiredMessage 
                        onUpgradeClick={onUpgradeClick}
                        variant="default"
                        trialDurationDays={trialStatus.trialDurationDays}
                      />
                    )}

                    {!trialStatus.isTrialUser && !trialStatus.isPaidUser && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-gray-700 mb-2">
                          <FiCheck className="text-gray-500" size={18} />
                          <span className="font-semibold">Standard Account</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Your account is active and ready to use.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subscription Info */}
              {userInfo?.subscriptionStatus && (
                <div className="pb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Subscription</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium px-2 py-1 rounded text-xs ${
                        userInfo.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                        userInfo.subscriptionStatus === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {userInfo.subscriptionStatus.toUpperCase()}
                      </span>
                    </div>
                    {userInfo.subscriptionPeriodStart && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Billing Period:</span>
                        <span className="font-medium text-xs">
                          {formatDate(userInfo.subscriptionPeriodStart)} - {formatDate(userInfo.subscriptionPeriodEnd)}
                        </span>
                      </div>
                    )}
                    {userInfo.paymentStatus && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment Status:</span>
                        <span className={`font-medium px-2 py-1 rounded text-xs ${
                          userInfo.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {userInfo.paymentStatus.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {userInfo.lastPaymentDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Payment:</span>
                        <span className="font-medium">{formatDate(userInfo.lastPaymentDate)}</span>
                      </div>
                    )}
                    {userInfo.cancelAtPeriodEnd && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Auto-Renewal:</span>
                        <span className="font-medium text-red-600">Cancelled</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Actions section removed - logout available in left panel */}
            </div>
          )}

          {/* Legal Links */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex justify-center space-x-4 text-sm">
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 underline"
              >
                Terms of Use
              </a>
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800 underline"
              >
                Privacy Policy
              </a>
            </div>
          </div>

          {/* Error State */}
          {!userInfoLoading && !userInfo && (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">Failed to load account information</p>
              <button
                onClick={handleRetryFetch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Retry Loading
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountModal;
